import { TextAaIcon, TextBIcon, TextItalicIcon, TextUnderlineIcon } from '@phosphor-icons/react';
import * as React from 'react';

import { cn } from '@/lib/utils';

export interface TextStyleState {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  allCaps: boolean;
}

export interface TextStyleTogglesProps {
  value: TextStyleState;
  onChange: (next: TextStyleState) => void;
  disabled?: boolean;
  className?: string;
}

const TOGGLES: Array<{
  key: keyof TextStyleState;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
}> = [
  { key: 'bold', label: 'Bold', Icon: TextBIcon },
  { key: 'italic', label: 'Italic', Icon: TextItalicIcon },
  { key: 'underline', label: 'Underline', Icon: TextUnderlineIcon },
  { key: 'allCaps', label: 'All caps', Icon: TextAaIcon },
];

/**
 * B / I / U / Aa as FOUR SEPARATE TOGGLES — not Mora's `SegmentedToggle`.
 *
 * Hightouch renders them as individually-rounded buttons with gaps rather than
 * a joined segment, and following them here is right for a stronger reason than
 * visual parity: a segmented group is a *semantic* claim of mutual exclusivity
 * — pick exactly one of these. Bold, Italic, Underline and All-caps are four
 * independent booleans that combine freely; bold-italic is an ordinary state,
 * not a contradiction. Joining them would tell the user something false about
 * how the control behaves, and `SEGMENTED_GROUP` exists precisely to make that
 * claim where it IS true (`Design | Safe zones | Preview`).
 */
export function TextStyleToggles({
  value,
  onChange,
  disabled,
  className,
}: TextStyleTogglesProps): JSX.Element {
  return (
    <div className={cn('flex items-center gap-1', className)} role="group" aria-label="Text style">
      {TOGGLES.map(({ key, label, Icon }) => {
        const active = value[key];
        return (
          <button
            key={key}
            type="button"
            aria-label={label}
            aria-pressed={active}
            disabled={disabled}
            onClick={() => onChange({ ...value, [key]: !active })}
            className={cn(
              'inline-flex size-8 items-center justify-center rounded-sm transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'disabled:pointer-events-none disabled:opacity-50',
              // Inactive toggles carry NO fill. Filling all four put a run of
              // four more rectangles directly under six filled property rows
              // and buried the one state that matters. Only the engaged toggle
              // earns a surface.
              active
                ? 'bg-selection-accent/10 text-selection-accent'
                : 'text-muted-foreground hover:bg-muted-foreground/10 hover:text-foreground',
            )}
          >
            <Icon className="size-3.5" />
          </button>
        );
      })}
    </div>
  );
}
